import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { X, Loader2, AlertTriangle } from "lucide-react";

interface IngressYAMLEditorProps {
  isOpen: boolean;
  initialYAML: string;
  tenantKey: string;
  onClose: () => void;
  onSuccess: (yaml: string, commitMessage: string) => Promise<void>;
}

interface YAMLFormData {
  yaml: string;
  commitMessage: string;
}

const IngressYAMLEditor: React.FC<IngressYAMLEditorProps> = ({
  isOpen,
  initialYAML,
  tenantKey,
  onClose,
  onSuccess,
}) => {
  const [submitting, setSubmitting] = React.useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<YAMLFormData>({
    defaultValues: { yaml: "", commitMessage: "" },
  });

  useEffect(() => {
    if (isOpen) {
      reset({
        yaml: initialYAML,
        commitMessage: `chore(ingress): update ${tenantKey} ingress config`,
      });
    }
  }, [isOpen, initialYAML, tenantKey, reset]);

  if (!isOpen) return null;

  const onSubmit = async (data: YAMLFormData) => {
    setSubmitting(true);
    try {
      await onSuccess(data.yaml, data.commitMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-3xl flex flex-col"
        style={{ maxHeight: "90vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0">
          <div>
            <h2 className="text-base font-bold text-slate-900">Edit Ingress YAML</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Tenant: <span className="font-mono font-semibold">{tenantKey}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-all"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {/* Warning banner */}
            <div className="flex items-start gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-800 leading-relaxed">
                Changes will be committed directly to the GitLab repository and trigger a pipeline.
                Ensure the YAML is valid before saving.
              </p>
            </div>

            {/* YAML editor */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                Ingress YAML <span className="text-red-500">*</span>
              </label>
              <textarea
                {...register("yaml", { required: "YAML content is required" })}
                rows={18}
                spellCheck={false}
                className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-xs font-mono text-green-300 leading-relaxed focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
                placeholder="apiVersion: networking.k8s.io/v1&#10;kind: Ingress&#10;..."
              />
              {errors.yaml && (
                <p className="mt-1 text-xs text-red-500">{errors.yaml.message}</p>
              )}
            </div>

            {/* Commit message */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                Commit Message <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                {...register("commitMessage", { required: "Commit message is required" })}
                className="w-full px-3 py-2.5 bg-slate-50 border border-slate-300 rounded-md text-sm text-slate-900 focus:ring-1 focus:ring-blue-100 focus:border-blue-300 outline-none"
                placeholder="chore(ingress): update tenant ingress config"
              />
              {errors.commitMessage && (
                <p className="mt-1 text-xs text-red-500">{errors.commitMessage.message}</p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100 shrink-0">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 text-sm font-semibold text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-all disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-all disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Committing...
                </>
              ) : (
                "Commit to GitLab →"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default IngressYAMLEditor;
